import {ffLazyLoading, hasFeatureFlag, logger, Time} from 'yougile-util'
import {
  DataObjectId,
  Entity,
  Hub,
  keepUnseenInfoInterval,
  maxDeleteUnseen,
  newsCompanyId,
  rootNodeId,
  SandboxTree,
  systemTagsIds,
  Tag
} from 'yougile-model'
import {PermissionsChecker} from '../client/permissions-checker'
import {Sync} from '../client/sync'

export class SystemEntities {
  public static all: Map<DataObjectId, SystemEntities> = new Map()

  public companyId: DataObjectId

  public createdByTagId: DataObjectId
  public seenByTagId: DataObjectId
  public taskCompletedTagId: DataObjectId
  public notifyTagId: DataObjectId
  public accessTagId: DataObjectId

  public static clean(): void {
    SystemEntities.all.clear()
  }

  public static get(companyId: DataObjectId): SystemEntities {
    if (companyId === newsCompanyId) {
      return undefined
    }
    if (!SystemEntities.all.has(companyId)) {
      const box = Sync.get({companyId})
      if (!box) {
        logger.error('No such sandbox')
        return undefined
      }
      SystemEntities.all.set(companyId, new SystemEntities(box))
    }
    return SystemEntities.all.get(companyId)
  }

  public isSystem(entity: Entity): boolean {
    return (
      [
        this.accessTagId,
        this.createdByTagId,
        this.notifyTagId,
        this.seenByTagId,
        this.taskCompletedTagId
      ].indexOf(entity.id) > -1
    )
  }

  constructor(box: SandboxTree) {
    this.companyId = box.companyId
    const allTags = box
      .getChildrenEntities(rootNodeId)
      .filter(e => e instanceof Tag) as Tag[]
    const findTag = (tagName: keyof typeof systemTagsIds): DataObjectId => {
      // oldest tag by name
      const tag = allTags
        .filter(t => t && t.title === tagName)
        .sort((a, b) => a.timestamp - b.timestamp)[0]
      if (!tag) {
        logger.warn(
          'System tag not found',
          tagName,
          box.get(box.companyId).title
        )
        return undefined
      } else {
        return tag.id
      }
    }

    this.createdByTagId = findTag('created-by')
    this.seenByTagId = findTag('seen-by')
    this.notifyTagId = findTag('notify')
    this.accessTagId = findTag('access')
    this.taskCompletedTagId = findTag('task-completed')

    setTimeout(() => {
      if (hasFeatureFlag(ffLazyLoading)) {
        return
      }
      this.cleanSeenBy(box)
      this.cleanDeleted(box, 'notify', this.notifyTagId)
      this.cleanDeleted(box, 'created-by', this.createdByTagId)
    }, 10 * 1000)
  }

  /**
   * Very old objects are considered seen by everyone.
   * So, there is no need to store them inside seen-by tag.
   * (Cleaning this tag saved a lot of space in database).
   */
  public cleanSeenBy(box: SandboxTree): void {
    if (!this.seenByTagId) {
      return
    }
    if (!PermissionsChecker.isAdmin({box})) {
      return
    }
    const company = box.get(box.companyId) as Hub
    const conn = box.getConnectionFrom(this.seenByTagId)
    if (!conn) {
      return
    }
    const oldTimestamp = Time.now() - keepUnseenInfoInterval
    let removedCount = 0
    conn.getAllConnected().forEach(id => {
      const obj = box.get(id)
      if (obj && obj.timestamp < oldTimestamp) {
        if (removedCount < maxDeleteUnseen) {
          removedCount++
          conn.disconnect(id)
        }
      }
    })
    if (removedCount > 0) {
      logger.log(
        `Cleaned ${removedCount} items from seen-by tag ${
          company && company.title
        }`
      )
      conn.commit()
    }
  }

  public cleanDeleted(
    box: SandboxTree,
    tagName: string,
    tagId: DataObjectId
  ): void {
    if (!tagId) {
      return
    }
    if (!PermissionsChecker.isAdmin({box})) {
      return
    }
    const company = box.get(box.companyId) as Hub
    const conn = box.getConnectionFrom(tagId)
    if (!conn) {
      return
    }
    let removedCount = 0
    conn.getAllConnected().forEach(id => {
      const obj = box.get(id)
      if (
        obj?.dataType === 'Hub' &&
        box.isDeleted(id) &&
        obj.id !== obj.title
      ) {
        removedCount++
        conn.disconnect(id)
      }
    })
    if (removedCount > 0) {
      logger.log(
        `Cleaned ${removedCount} items from tag ${tagName} ${
          company && company.title
        }`
      )
      conn.commit()
    }
  }
}
